/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { useUnpaidOrdersInRange, PayoutLike } from './useUnpaidOrdersInRange.ts';

interface UseRecentUnpaidOrdersResult {
  recentOrders: any[];
  recentOrdersLoading: boolean;
}

// The fast path for the dashboard charts below the earned-since-payment
// banner: a bounded recent window (default 35 days - enough to cover the
// 30-day bar chart's full range regardless of local timezone edge cases)
// instead of the collection's entire order history. The backend does real
// per-order work (matching Printful/Yoycol fulfillment costs), so this
// request is genuinely cheaper, not just smaller - it can finish well
// before useEarnedSincePayout's full-history fetch does, so the trend
// chart, 30-day chart, and recent sales list don't have to wait on it.
export function useRecentUnpaidOrders(
  selectedHandle: string | undefined,
  payouts: PayoutLike[],
  payoutsLoading: boolean,
  windowDays: number = 35
): UseRecentUnpaidOrdersResult {
  const [range] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - windowDays);
    return { start, end };
  });

  const { orders, loading } = useUnpaidOrdersInRange(selectedHandle, payouts, payoutsLoading, range);

  return { recentOrders: orders, recentOrdersLoading: loading };
}
